import { useCanvasStore } from "../../stores/canvasStore.js";

type WidgetSpec = { type: string; title?: string; position?: { x: number; y: number } } & Record<string, any>;
type Plan = { widgets: WidgetSpec[] };

type Bounds = { minX: number; minY: number; maxX: number; maxY: number };

const DEFAULT_SIZE = { width: 400, height: 240 };
const TYPE_SIZE: Record<string, { width: number; height: number }> = {
  runHeader: { width: 520, height: 240 },
  priceChart: { width: 500, height: 320 },
  newsSentiment: { width: 380, height: 300 },
  relationshipGraph: { width: 430, height: 400 },
  impactExplorer: { width: 380, height: 160 },
  profileHeader: { width: 800, height: 150 },
};

function sizeFor(type: string) {
  return TYPE_SIZE[type] || DEFAULT_SIZE;
}

function computeBoundsFromSpecs(widgets: WidgetSpec[]): Bounds | null {
  const positions = widgets
    .map((w, idx) => {
      const p = w.position;
      if (p && typeof p.x === "number" && typeof p.y === "number") return { p, s: sizeFor(w.type) };
      // fallback approximate default placement if missing
      return { p: { x: 100 + idx * 300, y: 200 }, s: sizeFor(w.type) };
    });
  if (positions.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  positions.forEach(({ p, s }) => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + s.width);
    maxY = Math.max(maxY, p.y + s.height);
  });
  return { minX, minY, maxX, maxY };
}

function expand(b: Bounds, pad = 40): Bounds {
  return { minX: b.minX - pad, minY: b.minY - pad, maxX: b.maxX + pad, maxY: b.maxY + pad };
}

export function placePlan(plan: Plan, opts?: { anchor?: "rightOfExisting" | "belowExisting"; margin?: number }) {
  const store = useCanvasStore.getState();
  const activeId = store.activeCanvasId;
  const canvas = activeId ? store.canvases.find((c) => c.id === activeId) : undefined;
  if (!canvas) return plan;

  const margin = Math.max(40, opts?.margin || 80);

  // Compute existing area (visible widgets only)
  const existing = canvas.widgets;
  const existingBounds = (() => {
    if (!existing || existing.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    existing.forEach((w) => {
      const s = sizeFor(w.type);
      minX = Math.min(minX, w.position.x);
      minY = Math.min(minY, w.position.y);
      maxX = Math.max(maxX, w.position.x + s.width);
      maxY = Math.max(maxY, w.position.y + s.height);
    });
    return { minX, minY, maxX, maxY } as Bounds;
  })();

  const planBounds = computeBoundsFromSpecs(plan.widgets) || { minX: 100, minY: 200, maxX: 1000, maxY: 600 };

  if (!existingBounds) {
    // Nothing on canvas, keep positions as-is
    return plan;
  }

  // Shift strategy: place plan to the right of existing content, aligned to top of existing
  const anchor = opts?.anchor || "rightOfExisting";
  let dx = 0, dy = 0;
  if (anchor === "rightOfExisting") {
    const desiredMinX = existingBounds.maxX + margin;
    dx = desiredMinX - planBounds.minX;
    // align top with existing content top
    const desiredMinY = Math.max(existingBounds.minY, 80);
    dy = desiredMinY - planBounds.minY;
  } else if (anchor === "belowExisting") {
    const desiredMinY = existingBounds.maxY + margin;
    dy = desiredMinY - planBounds.minY;
    const desiredMinX = Math.max(existingBounds.minX, 80);
    dx = desiredMinX - planBounds.minX;
  }

  const placedWidgets = plan.widgets.map((w, idx) => {
    const pos = w.position || { x: 100 + idx * 300, y: 200 };
    return { ...w, position: { x: pos.x + dx, y: pos.y + dy } };
  });

  return { widgets: placedWidgets } as Plan;
}

export function animateViewportTo(bounds: Bounds, durationMs = 350) {
  const store = useCanvasStore.getState();
  const activeId = store.activeCanvasId;
  const canvas = activeId ? store.canvases.find((c) => c.id === activeId) : undefined;
  if (!canvas) return;

  const pad = 60;
  const b = expand(bounds, pad);
  // Assume viewport base size ~800x600 for heuristic
  const viewW = 800, viewH = 600;
  const width = Math.max(1, b.maxX - b.minX);
  const height = Math.max(1, b.maxY - b.minY);
  const scaleX = viewW / width;
  const scaleY = viewH / height;
  const zoom = Math.min(1.2, Math.max(0.4, Math.min(scaleX, scaleY)));
  const centerX = (b.minX + b.maxX) / 2;
  const centerY = (b.minY + b.maxY) / 2;
  const target = { x: -centerX + viewW / 2, y: -centerY + viewH / 2, zoom };

  const start = canvas.viewport || { x: 0, y: 0, zoom: 1 };
  const steps = Math.max(1, Math.floor(durationMs / 16));
  let i = 0;
  const tick = () => {
    i++;
    const t = i / steps;
    const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad
    const vx = start.x + (target.x - start.x) * ease;
    const vy = start.y + (target.y - start.y) * ease;
    const vz = start.zoom + (target.zoom - start.zoom) * ease;
    store.updateViewport(activeId!, { x: vx, y: vy, zoom: vz });
    if (i < steps) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export function viewportForPlan(plan: Plan) {
  const b = computeBoundsFromSpecs(plan.widgets);
  return b ? expand(b, 60) : null;
}

